import { useState, useEffect, useMemo, useRef } from 'react';
import { X, Search } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import * as SimpleIcons from 'react-icons/si';
import * as FontAwesome from 'react-icons/fa';
import * as MaterialDesign from 'react-icons/md';
import { useDialogA11y } from '../ui/useDialogA11y';

interface IconPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (iconName: string) => void;
  currentIcon: string;
}

type TabType = 'popular' | 'brands' | 'education' | 'technology' | 'multimedia' | 'documents' | 'general';

const popularIcons = [
  'lucide:Link', 'lucide:ExternalLink', 'lucide:BookOpen', 'lucide:Video',
  'lucide:FileText', 'lucide:Download', 'lucide:Globe', 'lucide:Calendar',
  'lucide:Clock', 'lucide:Mail', 'lucide:Phone', 'lucide:Users',
  'lucide:Settings', 'lucide:HelpCircle', 'lucide:MessageSquare', 'lucide:Bell'
];

const brandIcons = [
  { name: 'si:SiGoogledrive', label: 'Google Drive' },
  { name: 'si:SiDropbox', label: 'Dropbox' },
  { name: 'si:SiYoutube', label: 'YouTube' },
  { name: 'si:SiNotion', label: 'Notion' },
  { name: 'si:SiSlack', label: 'Slack' },
  { name: 'si:SiDiscord', label: 'Discord' },
  { name: 'si:SiZoom', label: 'Zoom' },
  { name: 'si:SiMicrosoftteams', label: 'Teams' },
  { name: 'si:SiGithub', label: 'GitHub' },
  { name: 'si:SiGitlab', label: 'GitLab' },
  { name: 'si:SiFigma', label: 'Figma' },
  { name: 'si:SiCanva', label: 'Canva' },
  { name: 'si:SiTrello', label: 'Trello' },
  { name: 'si:SiMiro', label: 'Miro' },
  { name: 'si:SiGoogle', label: 'Google' },
  { name: 'si:SiGoogleclassroom', label: 'Google Classroom' },
  { name: 'si:SiGooglemeet', label: 'Google Meet' },
  { name: 'si:SiGoogleforms', label: 'Google Forms' },
  { name: 'si:SiMicrosoft', label: 'Microsoft' },
  { name: 'si:SiOnedrive', label: 'OneDrive' },
  { name: 'si:SiOutlook', label: 'Outlook' },
  { name: 'si:SiWhatsapp', label: 'WhatsApp' },
  { name: 'si:SiTelegram', label: 'Telegram' },
  { name: 'si:SiLinkedin', label: 'LinkedIn' },
  { name: 'si:SiFacebook', label: 'Facebook' },
  { name: 'si:SiInstagram', label: 'Instagram' },
  { name: 'si:SiTwitter', label: 'Twitter' },
  { name: 'si:SiSpotify', label: 'Spotify' },
  { name: 'si:SiApple', label: 'Apple' },
  { name: 'si:SiAmazon', label: 'Amazon' },
  { name: 'si:SiNetflix', label: 'Netflix' },
  { name: 'si:SiAsana', label: 'Asana' },
  { name: 'si:SiMonday', label: 'Monday' },
  { name: 'si:SiClickup', label: 'ClickUp' },
  { name: 'si:SiJira', label: 'Jira' },
  { name: 'si:SiConfluence', label: 'Confluence' },
  { name: 'si:SiEvernote', label: 'Evernote' },
  { name: 'si:SiAirtable', label: 'Airtable' },
  { name: 'si:SiBox', label: 'Box' },
  { name: 'si:SiIcloud', label: 'iCloud' }
];

const educationIcons = [
  'lucide:BookOpen', 'lucide:GraduationCap', 'lucide:Pencil', 'lucide:BookMarked',
  'lucide:Library', 'lucide:Calculator', 'lucide:Brain', 'lucide:School',
  'lucide:Backpack', 'lucide:Notebook', 'lucide:FileCheck', 'lucide:Award',
  'lucide:Trophy', 'lucide:Target', 'lucide:Lightbulb', 'lucide:Presentation'
];

const technologyIcons = [
  'lucide:Code', 'lucide:Terminal', 'lucide:Database', 'lucide:Server',
  'lucide:Cloud', 'lucide:Laptop', 'lucide:Monitor', 'lucide:Smartphone',
  'lucide:Wifi', 'lucide:Network', 'lucide:Cpu', 'lucide:HardDrive',
  'lucide:Bug', 'lucide:GitBranch', 'lucide:Package', 'lucide:Blocks'
];

const multimediaIcons = [
  'lucide:Video', 'lucide:Play', 'lucide:Pause', 'lucide:Music',
  'lucide:Volume2', 'lucide:Mic', 'lucide:Camera', 'lucide:Film',
  'lucide:Image', 'lucide:Headphones', 'lucide:Radio', 'lucide:Podcast',
  'lucide:Tv', 'lucide:Clapperboard', 'lucide:Aperture', 'lucide:Speaker'
];

const documentsIcons = [
  'lucide:FileText', 'lucide:File', 'lucide:Folder', 'lucide:FolderOpen',
  'lucide:Files', 'lucide:FileSpreadsheet', 'lucide:FileCode', 'lucide:FileImage',
  'lucide:FileVideo', 'lucide:FileAudio', 'lucide:Archive', 'lucide:FileDown',
  'lucide:FileUp', 'lucide:FilePlus', 'lucide:FileCheck', 'lucide:Paperclip'
];

export function IconPicker({ isOpen, onClose, onSelect, currentIcon }: IconPickerProps) {
  const [activeTab, setActiveTab] = useState<TabType>('popular');
  const [searchQuery, setSearchQuery] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(isOpen, onClose, dialogRef);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setActiveTab('popular');
    }
  }, [isOpen]);

  const getIconComponent = (iconString: string) => {
    const [library, iconName] = iconString.includes(':')
      ? iconString.split(':')
      : ['lucide', iconString];

    try {
      if (library === 'si') {
        return (SimpleIcons as any)[iconName];
      } else if (library === 'fa') {
        return (FontAwesome as any)[iconName];
      } else if (library === 'md') {
        return (MaterialDesign as any)[iconName];
      } else {
        return (LucideIcons as any)[iconName];
      }
    } catch {
      return LucideIcons.Link;
    }
  };

  const filteredIcons = useMemo(() => {
    let icons: string[] | { name: string; label: string }[] = [];

    switch (activeTab) {
      case 'popular':
        icons = popularIcons;
        break;
      case 'brands':
        icons = brandIcons;
        break;
      case 'education':
        icons = educationIcons;
        break;
      case 'technology':
        icons = technologyIcons;
        break;
      case 'multimedia':
        icons = multimediaIcons;
        break;
      case 'documents':
        icons = documentsIcons;
        break;
      case 'general':
        icons = Object.keys(LucideIcons)
          .filter(key => key !== 'default' && key !== 'createLucideIcon')
          .map(key => `lucide:${key}`);
        break;
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      if (activeTab === 'brands') {
        return (icons as { name: string; label: string }[]).filter(icon =>
          icon.label.toLowerCase().includes(query) ||
          icon.name.toLowerCase().includes(query)
        );
      } else {
        return (icons as string[]).filter(icon =>
          icon.toLowerCase().includes(query)
        );
      }
    }

    return icons;
  }, [activeTab, searchQuery]);

  const handleIconSelect = (iconName: string) => {
    onSelect(iconName);
    onClose();
  };

  const renderIcon = (iconData: string | { name: string; label: string }, index: number) => {
    const iconString = typeof iconData === 'string' ? iconData : iconData.name;
    const label = typeof iconData === 'string'
      ? iconString.split(':')[1] || iconString
      : iconData.label;

    const IconComponent = getIconComponent(iconString);
    if (!IconComponent) return null;

    const normalizedCurrent = currentIcon.includes(':') ? currentIcon : `lucide:${currentIcon}`;
    const isSelected = normalizedCurrent === iconString;

    return (
      <button
        key={index}
        onClick={() => handleIconSelect(iconString)}
        className={`p-4 rounded-lg transition-all duration-200 hover:bg-white/[0.04] group relative ${
          isSelected ? 'bg-cubus-lime/20 border-2 border-cubus-lime' : 'border-2 border-transparent'
        }`}
        title={label}
      >
        <div className="flex flex-col items-center gap-2">
          <IconComponent className="w-8 h-8 text-cubus-lime" />
          <span className="text-xs text-muted text-center line-clamp-1 max-w-[60px]">
            {label}
          </span>
        </div>
        {isSelected && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-cubus-lime rounded-full"></div>
        )}
      </button>
    );
  };

  if (!isOpen) return null;

  const tabs: { id: TabType; label: string }[] = [
    { id: 'popular', label: 'Populares' },
    { id: 'brands', label: 'Marcas' },
    { id: 'education', label: 'Educación' },
    { id: 'technology', label: 'Tecnología' },
    { id: 'multimedia', label: 'Multimedia' },
    { id: 'documents', label: 'Documentos' },
    { id: 'general', label: 'General' }
  ];

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title-iconpicker"
        tabIndex={-1}
        className="glass-surface rounded-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
      >
        <div className="sticky top-0 glass-surface border-b border-white/[0.08] px-6 py-4 flex items-center justify-between">
          <div>
            <h2 id="modal-title-iconpicker" className="text-xl font-bold text-white mb-1">Seleccionar Ícono</h2>
            <p className="text-sm text-muted">Elige un ícono para representar tu link</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/[0.04] rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-muted" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-white/[0.08]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar ícono..."
              className="w-full pl-10 pr-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:border-cubus-lime"
            />
          </div>
        </div>

        <div className="px-6 py-3 border-b border-white/[0.08] overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSearchQuery('');
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-cubus-lime text-cubus-bg'
                    : 'glass-deep text-muted hover:bg-white/[0.05]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {filteredIcons.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-faint mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-muted mb-2">
                No se encontraron íconos
              </h3>
              <p className="text-subtle">
                Intenta con otro término de búsqueda
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
              {filteredIcons.map((icon, index) => renderIcon(icon, index))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
